/**
 * Key-shape normalization between the frontend and the REST API.
 *
 * The backend validates camelCase bodies, while parts of this app were written
 * against a snake_case dialect. Outgoing bodies are camelized; incoming
 * payloads are "dualized" so every object exposes both `nameEn` and `name_en`,
 * which makes existing components work regardless of which casing the server
 * actually returns.
 */

export function camelKey(key: string): string {
  return key.replace(/[_-]+([a-zA-Z0-9])/g, (_m, c: string) => c.toUpperCase());
}

export function snakeKey(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, "$1_$2").replace(/-/g, "_").toLowerCase();
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date) &&
    !(value instanceof Blob) &&
    !(value instanceof FormData)
  );
}

/** Deeply converts every object key to camelCase. */
export function camelizeKeys<T = unknown>(input: unknown): T {
  if (Array.isArray(input)) return input.map((v) => camelizeKeys(v)) as unknown as T;
  if (!isPlainObject(input)) return input as T;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) out[camelKey(key)] = camelizeKeys(value);
  return out as T;
}

/**
 * Deeply adds both camelCase and snake_case aliases for every key so readers
 * written in either dialect resolve the same value.
 */
export function dualizeKeys<T = unknown>(input: unknown): T {
  if (Array.isArray(input)) return input.map((v) => dualizeKeys(v)) as unknown as T;
  if (!isPlainObject(input)) return input as T;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    const normalized = dualizeKeys(value);
    out[key] = normalized;
    const camel = camelKey(key);
    const snake = snakeKey(key);
    if (!(camel in out)) out[camel] = normalized;
    if (!(snake in out)) out[snake] = normalized;
  }
  return out as T;
}

/** Reads a field by name, tolerating camelCase / snake_case variants. */
export function field<T = any>(row: any, ...names: string[]): T | undefined {
  if (!row) return undefined;
  for (const name of names) {
    for (const candidate of [name, camelKey(name), snakeKey(name)]) {
      const value = row[candidate];
      if (value !== undefined && value !== null) return value as T;
    }
  }
  return undefined;
}

/** Accepts `{ items }`, `{ rows }`, `{ data: { items } }` or a bare array. */
export function readItems<T = any>(payload: any): T[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload as T[];
  const candidates = [payload.items, payload.rows, payload.results, payload.data];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as T[];
    if (candidate && Array.isArray(candidate.items)) return candidate.items as T[];
  }
  return [];
}
